"use client"

import React, { useEffect, useState } from "react"
import PrimaryButton from "../Ui/Buttons/PrimaryButton"
import SecondaryButton from "../Ui/Buttons/SecondaryButton"
import {
  setEndDate,
  setFilteredByExcludingTax,
  setFilteredDays,
  setSelectedStoreGroups,
  setSelectedStoreMarkets,
  setSelectedCampaignGroups,
  setSelectedCampaigns,
  setStartDate
} from "lib/store/services/analytics/slice"
import { useDispatch, useSelector } from "react-redux"
import {
  selectEndDate,
  selectFilteredByExcludingTax,
  selectFilteredDays,
  selectStartDate,
  selectStoreGroups,
  selectStoreMarkets,
  selectSelectedCampaignGroups,
  selectSelectedCampaigns
} from "lib/store/services/analytics/selectors"
import {
  CampaignIconWrapper, Grid,
  Layout,
  LayoutWrapper,
  MultiSelectWrapper,
  PeriodSelection,
  SelectWrapper
} from "./PeriodSelections.styled"
import { IconWrapper } from "./OrderChart.styled"
import MultiSelect from "../Ui/Form/MultiSelect"
import { StoreGroup } from "lib/types/generated/graphql-types"
import { ReactComponent as StoreGroupIcon } from "../../images/icons/circle-nodes.svg"
import { ReactComponent as StoreMarketIcon } from "../../images/icons/earth.svg"
import { ReactComponent as CampaignGroupIcon } from "../../images/icons/tag.svg"
import { ReactComponent as CampaignIcon } from "../../images/icons/badge-percent.svg"
import { getCountryName } from "helpers/countries"
import { Option } from "lib/types/common"
import { Divider, FilterGroup } from "./TopProductsFilters.styled"
import dayjs from "dayjs"
import FilterSectionDashboard from "./FilterSelectionDashboard"
import { Loader } from "../Ui/Loader"
import { useLazyQuery } from "@apollo/client"
import SEARCH_CAMPAIGNS from "graphql/queries/campaign/SearchCampaigns"
import SEARCH_CAMPAIGN_GROUPS from "graphql/queries/campaign/SearchCampaignGroups"
import esb from "elastic-builder"
import { selectSidebarCollapsed } from "lib/store/services/ui/selectors"

type Range = 30 | 7 | 2 | 1 | 0

interface PeriodButtonProps {
  range: Range
  currentRange: Range
  onClick: (range: Range) => void
  disabled: boolean
  label: string
}

const PeriodButton: React.FC<PeriodButtonProps> = ({
  range,
  currentRange,
  onClick,
  disabled,
  label
}) => {
  const isActive = range === currentRange
  if (isActive) {
    return (
      <PrimaryButton handleClick={() => onClick(range)}>
        {label}
      </PrimaryButton>
    )
  }

  return (
    <SecondaryButton handleClick={() => onClick(range)} overrideDisabled={disabled}>
      {label}
    </SecondaryButton>
  )
}

type Props = {
  timeZone: string
  storeGroups: StoreGroup[]
  loading: boolean
}

export default function PeriodSelections({ storeGroups, loading }: Props) {
  const dispatch = useDispatch()
  const filteredDays = useSelector(selectFilteredDays) as Range
  const startDate = useSelector(selectStartDate)
  const endDate = useSelector(selectEndDate)
  const [range, setRange] = useState<Range>(filteredDays)
  const selectedStoreGroups = useSelector(selectStoreGroups)
  const selectedStoreMarkets = useSelector(selectStoreMarkets)
  const selectedCampaigns = useSelector(selectSelectedCampaigns)
  const filteredByExcludingTax = useSelector(selectFilteredByExcludingTax)
  const [campaignGroupOptions, setCampaignGroupOptions] = useState<Option[]>([])
  const [campaignOptions, setCampaignOptions] = useState<Option[]>([])
  const selectedCampaignGroups = useSelector(selectSelectedCampaignGroups)
  const [allCampaigns, setAllCampaigns] = useState<
    { id: string; name: string; campaignGroupId: string }[]
  >([])
  const sideBarCollapsed = useSelector(selectSidebarCollapsed)


  const [searchCampaigns] = useLazyQuery(SEARCH_CAMPAIGNS, {
    onCompleted: (data) => {
      if (data?.searchCampaigns?.hits) {
        const campaigns = data.searchCampaigns.hits.map(
          (campaign: { id: string; name: string; campaignGroupId: string }) => ({
            id: campaign.id,
            name: campaign.name,
            campaignGroupId: campaign.campaignGroupId
          })
        )
        setAllCampaigns(campaigns)
        updateCampaignOptions(campaigns, selectedCampaignGroups)
      }
    },
    onError: (error) => {
      console.error("Failed to load campaigns:", error)
    }
  })

  const [searchCampaignGroups] = useLazyQuery(SEARCH_CAMPAIGN_GROUPS, {
    onCompleted: (data) => {
      if (data?.searchCampaignGroups?.hits) {
        const groupOptions = data.searchCampaignGroups.hits.map(
          (group: { id: string; name: string }) => ({
            value: group.id,
            label: group.name
          })
        )
        setCampaignGroupOptions(groupOptions)
      }
    },
    onError: (error) => {
      console.error("Failed to load campaign groups:", error)
    }
  })

  useEffect(() => {
    if (!startDate) {
      handleRangeChange(1)
    }
  }, [])

  useEffect(() => {
    // Only search for campaign groups if user has permissions
    searchCampaignGroups({
      variables: {
        from: 0,
        size: 1000,
        query: JSON.stringify(esb.matchAllQuery().toJSON())
      }
    })
  }, [])

  // Search for campaigns when selectedCampaignGroups changes
  useEffect(() => {
    if (selectedCampaignGroups.length > 0) {
      // Search for campaigns in selected campaign groups
      searchCampaigns({
        variables: {
          from: 0,
          size: 1000,
          query: JSON.stringify(
            esb
              .termsQuery(
                "campaignGroupId",
                selectedCampaignGroups.map((x) => x.value)
              )
              .toJSON()
          )
        }
      })
    } else {
      // Load all campaigns when no campaign groups are selected
      searchCampaigns({
        variables: {
          from: 0,
          size: 1000,
          query: JSON.stringify(esb.matchAllQuery().toJSON())
        }
      })
    }
  }, [selectedCampaignGroups])

  const handleRangeChange = (newRange: Range) => {
    setRange(newRange)
    const end = dayjs()
    const start = dayjs()
      .subtract(newRange - 1, "day")
      .startOf("day")
    const formattedStartDate = start.format("YYYY-MM-DDTHH:mm")
    const formattedEndDate = end.format("YYYY-MM-DDTHH:mm")
    dispatch(setStartDate(formattedStartDate))
    dispatch(setEndDate(formattedEndDate))
    dispatch(setFilteredDays(newRange))
  }

  const handleYesterdayChange = (newRange: Range) => {
    setRange(newRange)
    const end = dayjs()
      .subtract(newRange - 1, "day")
      .endOf("day")
    const start = dayjs()
      .subtract(newRange - 1, "day")
      .startOf("day")
    const formattedStartDate = start.format("YYYY-MM-DDTHH:mm")
    const formattedEndDate = end.format("YYYY-MM-DDTHH:mm")
    dispatch(setStartDate(formattedStartDate))
    dispatch(setEndDate(formattedEndDate))
    dispatch(setFilteredDays(newRange))
  }

  const getStoreMarketOptions = () => {
    const storeMarkets = storeGroups
      ?.map((storeGroup: StoreGroup) => storeGroup.storeMarkets)
      .flat()
    const countryCodes = storeMarkets?.map((storeMarket) => storeMarket?.countryCode)
    const uniqueCountryCodes = [...new Set(countryCodes)] as string[]
    return uniqueCountryCodes
      .map(
        (code: string) =>
          ({
            value: code,
            label: getCountryName(code)
          }) as Option
      )
      .sort((a, b) => a.label.localeCompare(b.label))
  }

  const handleClickFilterByExcludingTax = (value: boolean) => {
    dispatch(setFilteredByExcludingTax(value))
  }

  // Helper function to update campaign options based on selected campaign groups
  const updateCampaignOptions = (
    campaigns: { id: string; name: string; campaignGroupId: string }[],
    selectedGroups: Option[]
  ) => {
    let filteredCampaigns

    if (selectedGroups.length === 0) {
      // When no campaign groups are selected, show all campaigns
      filteredCampaigns = campaigns
        .map((campaign) => ({
          value: campaign.id,
          label: campaign.name
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    } else {
      // When campaign groups are selected, show only campaigns from those groups
      const selectedGroupIds = selectedGroups.map((group) => group.value)
      filteredCampaigns = campaigns
        .filter((campaign) => selectedGroupIds.includes(campaign.campaignGroupId))
        .map((campaign) => ({
          value: campaign.id,
          label: campaign.name
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    }

    setCampaignOptions(filteredCampaigns)
  }

  // Handle campaign group selection
  const handleCampaignGroupChange = (selectedGroups: Option[]) => {
    // Ensure we have plain objects for Redux
    const plainGroups = selectedGroups.map((group) => ({
      value: group.value,
      label: group.label
    }))

    dispatch(setSelectedCampaignGroups(plainGroups))
    updateCampaignOptions(allCampaigns, plainGroups)

    // Clear selected campaigns when campaign groups change
    dispatch(setSelectedCampaigns([]))
  }

  // Handle campaign selection and update Redux store
  const handleCampaignChange = (selectedCampaigns: Option[]) => {
    // Ensure we have plain objects for Redux and only store actual campaigns
    const plainCampaigns = selectedCampaigns.map((campaign) => ({
      value: campaign.value,
      label: campaign.label
    }))

    dispatch(setSelectedCampaigns(plainCampaigns))
  }

  // Update campaign options when allCampaigns changes
  useEffect(() => {
    if (allCampaigns.length > 0) {
      updateCampaignOptions(allCampaigns, selectedCampaignGroups)
    }
  }, [allCampaigns, selectedCampaignGroups])

  return (
    <LayoutWrapper>
      <SelectWrapper $ignoreLargeScreen={!sideBarCollapsed}>
        <Grid>
          <MultiSelectWrapper>
            <IconWrapper>
              <StoreGroupIcon />
            </IconWrapper>
            <MultiSelect
              selectAllEnabled={true}
              options={storeGroups?.map((storeGroup: StoreGroup) => ({
                value: storeGroup.id,
                label: storeGroup.name
              }))}
              placeholder={"All store groups"}
              value={selectedStoreGroups}
              setValue={(x) => dispatch(setSelectedStoreGroups(x))}
            />
          </MultiSelectWrapper>
          {getStoreMarketOptions()?.length > 0 && (
            <MultiSelectWrapper>
              <IconWrapper>
                <StoreMarketIcon />
              </IconWrapper>
              <MultiSelect
                placeholder={"Select store market"}
                options={getStoreMarketOptions()}
                value={selectedStoreMarkets}
                setValue={(x) => dispatch(setSelectedStoreMarkets(x))}
              />
            </MultiSelectWrapper>
          )}
          <MultiSelectWrapper>
            <CampaignIconWrapper>
              <CampaignGroupIcon />
            </CampaignIconWrapper>
            <MultiSelect
              selectAllEnabled={true}
              placeholder={"Select campaign groups"}
              options={campaignGroupOptions || []}
              value={selectedCampaignGroups}
              setValue={handleCampaignGroupChange}
            />
          </MultiSelectWrapper>
          <MultiSelectWrapper>
            <IconWrapper>
              <CampaignIcon />
            </IconWrapper>
            <MultiSelect
              selectAllEnabled={true}
              placeholder={"Select campaigns"}
              options={campaignOptions || []}
              value={selectedCampaigns.filter((c) =>
                allCampaigns.some((campaign) => campaign.id === c.value)
              )}
              setValue={handleCampaignChange}
            />
          </MultiSelectWrapper>
        </Grid>
        <Divider $ignoreLargeScreen={!sideBarCollapsed}></Divider>
        <FilterGroup>
          {filteredByExcludingTax ? (
            <>
              <PrimaryButton
                handleClick={() => handleClickFilterByExcludingTax(true)}
              >
                Excluding Tax
              </PrimaryButton>
              <SecondaryButton
                handleClick={() => handleClickFilterByExcludingTax(false)}
              >
                Including Tax
              </SecondaryButton>
            </>
          ) : (
            <>
              <SecondaryButton
                handleClick={() => handleClickFilterByExcludingTax(true)}
              >
                Excluding Tax
              </SecondaryButton>
              <PrimaryButton
                handleClick={() => handleClickFilterByExcludingTax(false)}
              >
                Including Tax
              </PrimaryButton>
            </>
          )}
        </FilterGroup>
      </SelectWrapper>
      <SelectWrapper>
        <Layout>
          <FilterSectionDashboard
            defaultFromDate={startDate}
            defaultToDate={endDate}
            setFromDate={(x) => {
              dispatch(setStartDate(x))
              setRange(0)
              dispatch(setFilteredDays(0))
            }}
            setToDate={(x) => {
              dispatch(setEndDate(x))
              setRange(0)
              dispatch(setFilteredDays(0))
            }}
          />
        </Layout>
        <Divider></Divider>
        <PeriodSelection>
          <PeriodButton
            range={1}
            currentRange={range}
            onClick={handleRangeChange}
            disabled={true}
            label={"Today"}
          ></PeriodButton>
          <PeriodButton
            range={2}
            currentRange={range}
            onClick={handleYesterdayChange}
            disabled={true}
            label={"Yesterday"}
          ></PeriodButton>
          <PeriodButton
            range={7}
            currentRange={range}
            onClick={handleRangeChange}
            disabled={true}
            label={"7 days"}
          ></PeriodButton>
          <PeriodButton
            range={30}
            currentRange={range}
            onClick={handleRangeChange}
            disabled={true}
            label={"30 days"}
          ></PeriodButton>
          {loading ? <Loader color={"black"}></Loader> : <></>}
        </PeriodSelection>
      </SelectWrapper>
    </LayoutWrapper>
  )
}
